import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BRAND } from "../lib/brand";
import { PageTitle, Panel, Field, inputCls, btnPrimary, btnGhost } from "./ui";
import { BrandLoader, Logo, BrandIcon } from "../components/site/Logo";

const LEGAL = [["legal_publisher_name", "Izdavač / pravno lice"], ["legal_address", "Adresa"], ["legal_responsible_person", "Odgovorna osoba (§ 18 MStV)"], ["legal_register", "Registar (sud, broj)"], ["legal_vat_id", "USt-IdNr."]];

const DemoPanel = ({ count, reload }) => {
  const run = async (method) => {
    if (method === "delete" && !window.confirm("Obrisati sav DEMO sadržaj?")) return;
    try {
      const { data } = await api[method]("/admin/demo");
      toast.success(method === "post" ? `Kreirano DEMO članaka: ${data.created}` : `Obrisano DEMO članaka: ${data.deleted}`);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="DEMO sadržaj (samo razvoj)" testId="demo-panel">
      <p className="text-sm text-muted-foreground">DEMO članci su označeni (is_demo), vidljivo obilježeni na portalu i odvojeni od stvarnog sadržaja. Obrišite ih prije javnog pokretanja.</p>
      <div className="mt-3 flex gap-2">
        <button onClick={() => run("post")} className={btnGhost} data-testid="demo-seed-button">Kreiraj DEMO članke</button>
        <button onClick={() => run("delete")} className={`${btnGhost} text-destructive`} data-testid="demo-delete-button">Obriši DEMO ({count ?? "?"})</button>
      </div>
    </Panel>
  );
};

export default function Settings() {
  const { can } = useAuth();
  const [s, setS] = useState(null);
  const [demoCount, setDemoCount] = useState(null);
  const load = () => {
    api.get("/admin/settings").then((r) => setS(r.data));
    api.get("/admin/dashboard").then((r) => setDemoCount(r.data.demo_articles));
  };
  useEffect(() => {
    load();
  }, []);
  if (!s) return <BrandLoader />;
  const set = (k) => (e) => setS({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings", Object.fromEntries(["contact_email", "legal_reviewed", ...LEGAL.map(([k]) => k)].map((k) => [k, s[k]])));
      toast.success("Postavke sačuvane");
    } catch (err) { toast.error(errMsg(err)); }
  };
  const testEmail = async () => {
    try {
      const { data } = await api.post("/admin/settings/test-email");
      data.ok ? toast.success("Test email poslan na vašu adresu") : toast.error(`Email nije poslan: ${data.info}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const lt = s.email_last_test;
  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <PageTitle title="Postavke" sub={`Okruženje: ${s.app_env}`} />
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Email isporuka" testId="email-settings">
          <p className="text-sm">Provajder: {s.email_configured ? "konfigurisan (serverska varijabla okruženja)" : "nije konfigurisan"}</p>
          <p className="mt-1 text-sm">Obavijesti o kontakt upitima: <code>{s.contact_notify_email || "—"}</code></p>
          <p className="mt-1 text-sm" data-testid="email-last-test">Zadnji test: {lt ? `${lt.ok ? "uspješan" : `neuspješan (${lt.info})`} · ${fmtDateTime(lt.at)}` : "nije izvršen"}</p>
          <button onClick={testEmail} className={`${btnGhost} mt-3`} data-testid="test-email-button">Pošalji test email meni</button>
        </Panel>
        <Panel title="Brend" testId="brand-settings">
          <div className="flex items-center gap-4"><Logo className="h-10" testId="settings-logo" /><BrandIcon className="h-12 w-12" testId="settings-icon" /></div>
          <p className="mt-3 text-xs text-muted-foreground">Službeni fajlovi: <code>{BRAND.logo}</code>, <code>{BRAND.icon}</code>. Zamjena: zamijenite fajlove u <code>/public/brand</code> (iste dimenzije/omjer) i regenerišite favicon varijante.</p>
        </Panel>
      </div>
      <form onSubmit={save}>
        <Panel title="Kontakt i pravni podaci (Impressum)" testId="legal-settings">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Javni kontakt email" htmlFor="st-email"><input id="st-email" type="email" required value={s.contact_email} onChange={set("contact_email")} className={inputCls} data-testid="settings-contact-email" /></Field>
            {LEGAL.map(([k, l]) => (
              <Field key={k} label={l} htmlFor={`st-${k}`}><input id={`st-${k}`} value={s[k] || ""} onChange={set(k)} className={inputCls} data-testid={`settings-${k}`} /></Field>
            ))}
          </div>
          <label className="mt-4 flex items-center gap-2 text-sm"><input type="checkbox" checked={!!s.legal_reviewed} onChange={set("legal_reviewed")} data-testid="settings-legal-reviewed" /> Pravni podaci su potpuni i pravno pregledani – objavi Impressum</label>
          <button className={`${btnPrimary} mt-4`} data-testid="settings-save-button">Sačuvaj postavke</button>
        </Panel>
      </form>
      {can("demo.manage") && demoCount > 0 && <DemoPanel count={demoCount} reload={load} />}
    </div>
  );
}
